import { CSSProperties, JSX } from "react";

interface ContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  className?: string;
  id?: string;
  style?: CSSProperties;
  role?: string;
  ariaLabelledBy?: string;
  ref?: React.Ref<HTMLDivElement>;
  decorativeContainer?: boolean;
}

type ContainerPropsWithChildren = ContainerProps & {
  decorativeContainer?: false;
  children: React.ReactNode;
};

type ContainerPropsWithoutChildren = ContainerProps & {
  decorativeContainer: true;
  children?: React.ReactNode;
};

type ValidContainerProps =
  | ContainerPropsWithChildren
  | ContainerPropsWithoutChildren;

export const Container = (props: ValidContainerProps): JSX.Element => {
  const { children, decorativeContainer, ...rest } = props;

  if (!decorativeContainer && (children === undefined || children === null)) {
    // eslint-disable-next-line
    console.warn(
      "Container: children prop is required when decorativeContainer is not true"
    );
  }

  return <div {...rest}>{!decorativeContainer ? children : null}</div>;
};

/**
 * A flexible container component that wraps content in a div element.
 * Extends all standard HTML div attributes and provides additional props
 * for accessibility and styling. Acts as a foundational layout component
 * that can be customized through props while maintaining semantic HTML structure.
 *
 * @component
 * @param {ValidContainerProps} props - The component props
 * @param {React.ReactNode} props.children - Child elements to render inside the container (required when decorativeContainer is not true)
 * @param {string} [props.className] - CSS class names for styling
 * @param {string} [props.id] - Unique identifier for the container
 * @param {CSSProperties} [props.style] - Inline CSS styles
 * @param {string} [props.role] - ARIA role for accessibility
 * @param {string} [props.ariaLabelledBy] - ID of the element that labels this container
 * @param {React.Ref<HTMLDivElement>} [props.ref] - Ref to the underlying div element
 * @param {boolean} [props.decorativeContainer=false] - If true, renders an empty div without children (useful for layout purposes). When false or undefined, children is required.
 *
 * @example
 * // Basic usage (children required)
 * <Container className="my-container">
 *   <p>Content goes here</p>
 * </Container>
 *
 * @example
 * // With accessibility props (children required)
 * <Container
 *   role="main"
 *   ariaLabelledBy="header-id"
 *   className="main-content"
 * >
 *   <h1 id="header-id">Page Title</h1>
 *   <p>Page content</p>
 * </Container>
 *
 * @example
 * // Decorative container for layout spacing (children optional)
 * <Container decorativeContainer className="spacer" style={{ height: '20px' }} />
 *
 * @example
 * // With ref forwarding (children required)
 * const containerRef = useRef<HTMLDivElement>(null);
 * <Container ref={containerRef} className="scrollable-container">
 *   <p>Scrollable content</p>
 * </Container>
 *
 * @throws {console.warn} Warns when children is not provided for non-decorative containers
 */
